import type { LoopState, PermanentState } from "../types"
import { formatDay, formatCalendarDate } from "../game/calendarEngine"
import { getRitualDeadlineDay } from "../game/loopEngine"
import { FrameCorners } from "../components/Framed"

interface VictoryScreenProps {
  permanent: PermanentState
  loop: LoopState
  onBeginAgain: () => void
  onMainMenu: () => void
}

/**
 * The coronation ending — the one morning that does not repeat. Reached only by
 * throwing down the Necromancer before Day 512, which per the final-loop
 * structure means every allied realm subjected, every hostile stronghold
 * cleared, the anti-player severed, and the citadel opened by the door he gave.
 * The screen deliberately mirrors the DeathScreen the player has seen so many
 * times, and then refuses to offer the "Awaken" that always followed it: this
 * time the world does not reset around them.
 */
export function VictoryScreen({
  permanent,
  loop,
  onBeginAgain,
  onMainMenu,
}: VictoryScreenProps) {
  const daysToSpare = Math.max(0, getRitualDeadlineDay(loop.loopNumber) - loop.currentDay)

  return (
    <div className="screen victory-screen framed">
      <FrameCorners />
      <h1>The Sun Holds</h1>
      <p className="screen-lede">
        In the ritual chamber, before the five hundred and twelfth day, the
        Necromancer is thrown down — and the working he had almost finished comes
        apart around him. Above the citadel the bruised, failing sky steadies,
        and then, for the first time in longer than you can count, the light
        comes back. Full, and warm, and ordinary. The dead he was holding upright
        sit down and are only dead. It is over.
      </p>
      <p>
        You reached the chamber on {formatDay(loop.currentDay)} —{" "}
        {daysToSpare === 0
          ? "on the very last of your days"
          : `with ${daysToSpare} day${daysToSpare === 1 ? "" : "s"} to spare`}
        . When you climb out of the citadel by the way Aldric gave you, the
        morning you walk into is not one you have seen before. It does not begin
        again. It simply continues, into an afternoon, and an evening, and a
        tomorrow that will be a different day.
      </p>
      <p>
        The realms you spent so many deaths learning to unite crown you for it —
        the crown, the elves of two courts, the dwarven Thane, the wardens of the
        dead, the Khan of the reconciled steppe, the Tower that had not agreed in
        a century. You did not win the world with a sword. You won it by becoming,
        across uncounted lifetimes nobody else remembers, the one person the whole
        of it would follow. Somewhere, a grey-haired man who is finally allowed to
        grow old raises a cup to a morning that is, at last, only itself.
      </p>
      <dl className="status-line">
        <dt>Campaign completed</dt>
        <dd>Loop {loop.loopNumber}</dd>
        <dt>Won on</dt>
        <dd>{formatCalendarDate(loop.currentDay)}</dd>
        <dt>Days to spare</dt>
        <dd>{daysToSpare}</dd>
        <dt>Deaths it took</dt>
        <dd>{permanent.totalDeaths}</dd>
      </dl>
      <div className="world-map-actions">
        <button type="button" onClick={onBeginAgain}>
          Begin the Loop Again
        </button>
        <button type="button" onClick={onMainMenu}>
          Return to the Main Menu
        </button>
      </div>
      <p className="marginalia">
        The morning you carry your knowledge back into is yours to keep. Every
        ability, every road, every name is still with you — should you ever wish
        to walk it all again.
      </p>
    </div>
  )
}
